import { createServerFn } from "@tanstack/react-start";

export interface CompareFeedItem {
  id: string;
  place: string | null;
  elomrade: string | null;
  avtalstyp: string | null;
  forbrukning: number | null;
  created_at: string;
}

const AVTALSTYPER = ["manadspris", "timpris", "kvartspris", "fast"] as const;
type Avtalstyp = (typeof AVTALSTYPER)[number];

export interface CompareSearchInput {
  postalCode?: string | null;
  elomrade?: string | null;
  avtalstyp?: Avtalstyp | null;
  forbrukning?: number | null;
}

export function parseCompareSearch(data: unknown): CompareSearchInput {
  if (!data || typeof data !== "object") throw new Error("Ogiltig indata");
  const d = data as Record<string, unknown>;
  const opt = <T>(v: unknown, ok: (x: unknown) => x is T): T | null => {
    if (v === undefined || v === null) return null;
    if (!ok(v)) throw new Error("Ogiltig indata");
    return v;
  };
  return {
    postalCode: opt(d["postalCode"], (x): x is string => typeof x === "string" && x.length <= 10),
    elomrade: opt(d["elomrade"], (x): x is string => typeof x === "string" && /^SE[1-4]$/.test(x)),
    avtalstyp: opt(d["avtalstyp"], (x): x is Avtalstyp => AVTALSTYPER.includes(x as Avtalstyp)),
    forbrukning: opt(
      d["forbrukning"],
      (x): x is number => typeof x === "number" && Number.isInteger(x) && x >= 0 && x <= 1_000_000,
    ),
  };
}

/**
 * Loggar en genomförd prisjämförelse för livefeeden på startsidan. Endast
 * kommunnamn (ur vår kommunlista), elområde, avtalstyp och förbrukning
 * sparas – aldrig postnummer.
 */
export const logCompareSearch = createServerFn({ method: "POST" })
  // Handskriven validering i stället för zod: funktionen anropas från
  // startsidan och elavtalswidgeten, och zod (~55 kB) ska inte laddas där.
  .inputValidator((data: unknown) => parseCompareSearch(data))
  .handler(async ({ data }) => {
    const pc = (data.postalCode ?? "").replace(/\D+/g, "");
    let place: string | null = null;
    if (pc.length === 5) {
      const { lookupMunicipality, matchMunicipality } = await import("./postalMunicipality.server");
      place = matchMunicipality(await lookupMunicipality(pc).catch(() => null));
    }
    const elomrade = data.elomrade ?? null;
    if (!place && !elomrade) return { ok: false };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.from("compare_feed").insert({
      place,
      elomrade,
      avtalstyp: data.avtalstyp ?? null,
      forbrukning: data.forbrukning ?? null,
    });
    return { ok: true };
  });

/** Senaste prisjämförelserna för livefeeden på startsidan. */
export const getCompareFeed = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: CompareFeedItem[] }> => {
    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(process.env["SUPABASE_URL"]!, process.env["SUPABASE_PUBLISHABLE_KEY"]!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data } = await sb
      .from("compare_feed")
      .select("id, place, elomrade, avtalstyp, forbrukning, created_at")
      .order("created_at", { ascending: false })
      .limit(20);
    return { items: (data ?? []) as CompareFeedItem[] };
  },
);
